import React, {useCallback, useState} from 'react';
import produce from 'immer';
import {createResource, type ResourceType} from 'api/generated';
import {useCreateFile} from 'hooks';
import {Button, InputFile, Modal} from 'components';

import ModalResourceItem from './Modal.Resource.Item';

interface IModalResourceAddProps {
  visible: boolean;
  onUploaded: (resource: ResourceType) => void;
  onClose: () => void;
  onConfirm: (resources: ResourceType[]) => void;
  addedResources: ResourceType[];
  resources: ResourceType[];
  projectUid?: string;
}

const ModalResourceAdd = ({visible, onClose, onConfirm, onUploaded, addedResources, resources, projectUid}: IModalResourceAddProps) => {
  const [toAdd, setToAdd] = useState<ResourceType[]>([]);

  const {handleUpload, inputRef, isPending, progress} = useCreateFile({
    createFile: createResource,
    callback: onUploaded,
    projectUid,
  });

  const handleClicked = useCallback((resource: ResourceType) => {
    setToAdd(
      produce(draft => {
        const index = draft.findIndex(m => m.uid === resource.uid);
        if (index === -1) {
          draft.length = 0;
          draft.push(resource);
        } else {
          draft.splice(index, 1);
        }
      }),
    );
  }, []);

  const handleConfirm = async () => {
    onConfirm(toAdd);
    await new Promise(resolve => setTimeout(resolve, 500));
    setToAdd([]);
  };

  return (
    <Modal visible={visible} onClose={onClose} backdropClose={!isPending} size="large" fullHeight>
      <div className="flex flex-col h-full">
        <InputFile
          inputRef={inputRef}
          buttonText="+ Add image file"
          accept="image/*"
          supportedFormats={['jpg', 'png']}
          onUpload={handleUpload}
          loading={isPending}
          progress={progress}
        />
        <div className="flex flex-col flex-1 justify-between">
          <ul className="mt-2 grid grid-cols-2 sm:grid-cols-6 gap-y-1">
            {resources.map(resource => {
              const checked = !!toAdd.find(s => s.uid === resource.uid);
              const disabled = !!addedResources.find(s => s.uid === resource.uid);
              return (
                <ModalResourceItem key={resource.uid} resource={resource} readOnly onClick={handleClicked} checked={checked} disabled={disabled} />
              );
            })}
          </ul>
          <div className="flex justify-between items-center">
            <Button text="Cancel" variant="secondary" size="extra-small" onClick={onClose} disabled={isPending} />
            <Button text="Confirm" size="extra-small" onClick={handleConfirm} disabled={isPending || toAdd.length === 0} />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default React.memo(ModalResourceAdd);
